import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import { Link } from 'react-router-dom';
import { FormattedDate, injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import SupportedLanguages from 'Features/Product/components/ProductAboutSection/SupportedLanguages';
import FantasyVerseStripBanner from 'Components/FantasyVerse/FantasyVerseStripBanner';

import 'Features/Product/components/ProductAboutSection/ProductDetails.scss';

export const titleMessages = defineMessages({
  publishers: {
    id: 'new.bookdetails.publishers',
    defaultMessage: '{count, plural, one {Publisher} other {Publishers}}',
  },
  authors: {
    id: 'new.bookdetails.authors',
    defaultMessage: '{count, plural, one {Author} other {Authors}}',
  },
  artists: {
    id: 'new.bookdetails.artists',
    defaultMessage: '{count, plural, one {Artist} other {Artists}}',
  },
  formats: {
    id: 'new.bookdetails.formats',
    defaultMessage: '{count, plural, one {Format} other {Formats}}',
  },
  files: {
    id: 'new.bookdetails.files',
    defaultMessage: '{count, plural, one {File} other {Files}}',
  },
  pages: {
    id: 'new.bookdetails.pages',
    defaultMessage: 'Pages',
  },
  isbn: {
    id: 'new.bookdetails.isbn',
    defaultMessage: 'ISBN',
  },
  age: {
    id: 'new.bookdetails.age',
    defaultMessage: 'Age',
  },
  released: {
    id: 'new.bookdetails.released',
    defaultMessage: 'Publication Date',
  },
  link: {
    id: 'new.bookdetails.Link',
    defaultMessage: 'Link',
  },
  supported_languages: {
    id: 'new.bookdetails.supportedlanguages',
    defaultMessage: 'Languages',
  },
});

// Had to create a new capitlize method because the lodash one has changed from amen
// use lodash CamelCase
const capitalize = tag => tag.charAt(0).toUpperCase() + tag.slice(1);

const listElement = (title, func, label) =>
  <dl key={title || label} className="product-details">
    <dt>{label || capitalize(title)}:</dt>
    <dd>{func()}</dd>
  </dl>;

const validLink = (link) => {
  let url = link;

  if (!link.match('^(http|https)://.*$')) {
    url = `http://${link}`;
  }
  return url;
};

const searchLink = (key, slug, label) => <Link to={`/${key}/${slug}`}>{label || slug}</Link>;

// TODO Refactor - pass in whole product, stop repeated blocks of code

function BookDetails({
  data,
  released,
  links,
  intl,
  currentDrm,
  downloadTypes,
  type,
}) {
  const { formatMessage } = intl;

  const {
    age,
    artists,
    authors,
    files,
    isbn,
    languages,
    pages,
    publishers,
    collections,
  } = data;

  const { url } = links;

  let ProductType = 'Book';
  if (type === 'comic') {
    ProductType = 'Comic';
  }
  if (type === 'graphic-novel') {
    ProductType = 'Graphic Novel';
  }

  const isPartOfFantasyverse = includes(collections, 'FantasyVerse');

  return (
    <div className="product-details-container">
      {currentDrm !== 'utalk' &&
        <React.Fragment>
          {isPartOfFantasyverse &&
            <FantasyVerseStripBanner />
          }
          <h3 className="section-heading">
            <FormattedMessage
              id="new.bookdetails.title.book"
              defaultMessage="{type} Details"
              tagName="span"
              values={{
                type: ProductType,
              }}
            />
          </h3>
          <div className="product-details-content">
            <div className="product-details-left">
              {!isEmpty(languages) &&
                listElement('supportedLanguages', () =>
                  <div>
                    <SupportedLanguages languages={languages} />
                  </div>, formatMessage(titleMessages.supported_languages),
                )
              }

              {!isEmpty(released) &&
                listElement('released', () =>
                  <div>
                    <FormattedDate value={released} year="numeric" month="long" />
                  </div>, formatMessage(titleMessages.released),
                )
              }

              {!isEmpty(publishers) &&
                listElement('publishers', () =>
                  publishers.map((publisher, i) => {
                    const { name, link, slug: facetSlug } = typeof publisher === 'string' ? { name: publisher, link: false } : publisher;
                    return (
                      <span key={name}>
                        {i > 0 && <br />}
                        {link ? searchLink('publishers', facetSlug, name) : name}
                      </span>
                    );
                  }), formatMessage(titleMessages.publishers, { count: publishers.length }),
                )
              }

              {!isEmpty(isbn) &&
                listElement('isbn', () =>
                  <React.Fragment>
                    {isbn}
                  </React.Fragment>, formatMessage(titleMessages.isbn),
                )
              }

              {age.book > 0 &&
                listElement('age', () =>
                  <FormattedMessage
                    id="new.bookdetails.age.age-book"
                    defaultMessage="Suitable for readers aged {age}+"
                    values={{ age: age.book }}
                  />, formatMessage(titleMessages.age),
                )
              }

              {links && (links.url) && listElement('link', () =>
                <div>
                  {url &&
                    <div>
                      <a
                        href={validLink(links.url)}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                      >
                        <FormattedMessage id="new.bookdetails.website" defaultMessage="View the website" tagName="span" />
                        <FontAwesomeIcon icon={faUpRightFromSquare} className="ml-1" />
                      </a>
                    </div>
                  }
                </div>, formatMessage(titleMessages.link),
              )}
            </div>

            <div className="product-details-right">
              {!isEmpty(authors) &&
                listElement('authors', () => authors.map((author, i) =>
                  <span key={author}>
                    {i > 0 && <br />}{author}
                  </span>,
                ), formatMessage(titleMessages.authors, { count: authors.length }),
                )
              }

              {!isEmpty(artists) &&
                listElement('artists', () => authors.map((artist, i) =>
                  <span key={artist}>
                    {i > 0 && <br />}{artist}
                  </span>,
                ), formatMessage(titleMessages.artists, { count: artists.length }),
                )
              }

              {!isEmpty(downloadTypes) &&
                listElement('downloadTypes', () => downloadTypes.map((format, i) =>
                  <span key={format}>
                    {i > 0 && <br />}{format.toUpperCase()}
                  </span>,
                ), formatMessage(titleMessages.formats, { count: downloadTypes.length }),
                )
              }

              {files > 0 &&
                listElement('files', () =>
                  <React.Fragment>
                    {files}
                  </React.Fragment>, formatMessage(titleMessages.files, { count: files.length }),
                )
              }

              {pages > 0 &&
                listElement('pages', () =>
                  <React.Fragment>
                    {pages}
                  </React.Fragment>, formatMessage(titleMessages.pages),
                )
              }
            </div>
          </div>
        </React.Fragment>
      }
    </div>
  );
}

BookDetails.propTypes = {
  data: PropTypes.shape({}),
  released: PropTypes.string,
  links: PropTypes.shape({
    url: PropTypes.string,
    steam: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  currentDrm: PropTypes.string,
  downloadTypes: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.string,
};

BookDetails.defaultProps = {
  data: {},
  released: '',
  links: {
    steam: {},
  },
  currentDrm: '',
  downloadTypes: [],
  type: 'book',
};

export default injectIntl(BookDetails);
